import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import { TAgetBrandStateStats, TAgetBrandStateDurationStats } from "../../../services/brandAPI"
import { getBrandStateLabel } from "../../../utils/brandStateLabels"
import { useNavigate } from "react-router-dom"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { Sankey, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts"

interface BrandStateHistory {
  brandId: string
  brandName: string
  currentState: string
  currentHasBalanceLoaded: boolean
  currentHasMultipleBalanceLoads: boolean
  totalStateChanges: number
  stateHistory: {
    state: string
    date: string
    timestamp: string
    has_balance_loaded: boolean
    has_multiple_balance_loads: boolean
  }[]
}

type StateDurationMetricsRaw = {
  average_duration: number
  min_duration: number
  max_duration: number
  median_duration: number
  total_occurrences: number
  total_duration: number
}

type DurationRecord = {
  date: string
  period: "daily" | "weekly" | "monthly"
  state_durations: Record<string, StateDurationMetricsRaw>
}

type SankeyNodeT = { name: string; state: string; fill: string }
type SankeyLinkT = {
  source: number
  target: number
  value: number
  color?: string
  __fromState?: string
}
type SankeyGraphT = { nodes: SankeyNodeT[]; links: SankeyLinkT[] }

type LegendNode = { index: number; name: string; color: string; centerX: number }

interface UpdatedBrandStateResponse {
  success: boolean
  data: BrandStateHistory[]
  aggregatedData: {
    daily: DailyBrandStateData[]
    weekly: WeeklyBrandStateData[]
    monthly: MonthlyBrandStateData[]
  }
  dateRange: {
    startDate: string
    endDate: string
  }
}

interface DailyBrandStateData {
  date: string
  stateStats: { [state: string]: number }
  stateTransitions: { from: string; to: string; count: number }[]
}

interface WeeklyBrandStateData {
  weekNumber: number
  startDate: string
  endDate: string
  stats: { [state: string]: number }
  transitions: { [state: string]: { incoming: number; outgoing: number } }
  days: DailyBrandStateData[]
}

interface MonthlyBrandStateData {
  monthNumber: number
  year: number
  startDate: string
  endDate: string
  stats: { [state: string]: number }
  transitions: { [state: string]: { incoming: number; outgoing: number } }
  weeks: WeeklyBrandStateData[]
}

const BrandStats = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<"daily" | "weekly" | "monthly">("monthly")
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [selectedDays, setSelectedDays] = useState<number>(-1)
  const [startDate, setStartDate] = useState<Date>(() => new Date("2020-09-01T00:00:00.000Z"))

  const [brandStateData, setBrandStateData] = useState<UpdatedBrandStateResponse | null>(null)
  const [durationRecords, setDurationRecords] = useState<DurationRecord[]>([])
  const [dailyData, setDailyData] = useState<DailyBrandStateData[]>([])
  const [weeklyData, setWeeklyData] = useState<WeeklyBrandStateData[]>([])
  const [monthlyData, setMonthlyData] = useState<MonthlyBrandStateData[]>([])
  const [sankeyTopN, setSankeyTopN] = useState<number>(12)
  const legendMapRef = useRef<Map<number, LegendNode>>(new Map())
  const legendTimer = useRef<any>(null)
  const [legendList, setLegendList] = useState<LegendNode[]>([])
  const [showBrandModal, setShowBrandModal] = useState(false)
  const [selectedList, setSelectedList] = useState<{
    type: "total" | "incoming" | "outgoing" | "flow"
    state: string
    fromState?: string
    periodLabel: string
    dateRange: { startISO: string; endISO: string }
    brands: { brand_id: string; brandName?: string }[]
  } | null>(null)

  useEffect(() => {
    dispatch(setPageTitle("Brand State Statistics"))
  }, [dispatch])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const loadStatistics = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)

    try {
      const regularResponse = await TAgetBrandStateStats(
        startDate.toISOString(),
        endDate.toISOString(),
        abortControllerRef.current.signal,
      )
      setBrandStateData(regularResponse)

      const durationResponse = await TAgetBrandStateDurationStats(
        startDate.toISOString(),
        endDate.toISOString(),
        abortControllerRef.current.signal,
        viewMode,
      )

      if (durationResponse && durationResponse.records && durationResponse.records.length > 0) {
        setDurationRecords(durationResponse.records)
      } else {
        setDurationRecords([])
      }

      if (regularResponse.data) {
        setDailyData(regularResponse.aggregatedData.daily)
        setWeeklyData(regularResponse.aggregatedData.weekly)
        setMonthlyData(regularResponse.aggregatedData.monthly)
      }

      setLoading(false)
    } catch (error: unknown) {
      const err = error as { name?: string; code?: string; response?: { status?: number } }
      if (err?.name === "AbortError" || err?.code === "ERR_CANCELED") {
        setLoading(false)
        return
      }

      if (err?.response && err.response.status === 401) {
        navigate("/login")
        return
      }
      setError("Error loading statistics")
      setLoading(false)
    }
  }, [startDate, endDate, viewMode, navigate])

  useEffect(() => {
    loadStatistics()
  }, [loadStatistics])

  const reportNodeLayout = useCallback((entry: LegendNode) => {
    legendMapRef.current.set(entry.index, entry)
    if (legendTimer.current) {
      clearTimeout(legendTimer.current)
    }
    legendTimer.current = setTimeout(() => {
      const arr = Array.from(legendMapRef.current.values()).sort((a, b) => a.centerX - b.centerX)
      setLegendList(arr)
    }, 0)
  }, [])

  const getStateHexColor = (state: string) => {
    const map: Record<string, string> = {
      registered_has_no_campaigns: "#64748B",
      campaign_in_draft: "#A78BFA",
      campaign_created_balance_not_loaded: "#F59E0B",
      loaded_balance_has_no_campaigns: "#10B981",
      campaign_created_has_no_applications: "#60A5FA",
      campaign_created_balance_loaded_has_no_applications: "#22D3EE",
      campaign_created_received_applications_profile_not_approved: "#FB7185",
      campaign_created_received_applications_profile_approved: "#34D399",
      campaign_created_received_applications_collaboration_completed: "#F472B6",
      has_balance_loaded: "#14B8A6",
      loaded_balance_multiple_times: "#6366F1",
    }
    return map[state] || "#9CA3AF"
  }

  type Transition = { from: string; to: string; count: number }

  const flattenDailyTransitions = (days: DailyBrandStateData[]) =>
    days.flatMap((d) => d.stateTransitions ?? [])

  const mergeTransitions = (transitions: Transition[]): Transition[] => {
    const m = new Map<string, number>()
    for (const t of transitions) {
      if (!t?.from || !t?.to) {
        continue
      }
      const key = `${t.from}→${t.to}`
      m.set(key, (m.get(key) || 0) + (t.count || 0))
    }
    return Array.from(m.entries()).map(([k, v]) => {
      const [from, to] = k.split("→")
      return { from, to, count: v }
    })
  }

  const transitionsForView: Transition[] = useMemo(() => {
    if (viewMode === "daily") {
      return flattenDailyTransitions(dailyData)
    }
    if (viewMode === "weekly") {
      return mergeTransitions(weeklyData.flatMap((w) => flattenDailyTransitions(w.days)))
    }
    return mergeTransitions(
      monthlyData.flatMap((m) => m.weeks.flatMap((w) => flattenDailyTransitions(w.days))),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, dailyData, weeklyData, monthlyData])

  const buildSankeyFromTransitions = (
    transitions: Transition[],
    topN = 12,
    minValue = 1,
  ): SankeyGraphT => {
    const merged = mergeTransitions(transitions)
      .filter((t) => (t.count || 0) >= minValue)
      .sort((a, b) => b.count - a.count)
      .slice(0, topN)

    const nodeStates = new Set<string>()
    merged.forEach((t) => {
      nodeStates.add(t.from)
      nodeStates.add(t.to)
    })

    const nodes: SankeyNodeT[] = Array.from(nodeStates).map((state) => ({
      name: getBrandStateLabel(state),
      state,
      fill: getStateHexColor(state),
    }))

    const idx = new Map(nodes.map((n, i) => [n.state, i]))

    const links: SankeyLinkT[] = merged.flatMap((t) => {
      const sIdx = idx.get(t.from)
      const tIdx = idx.get(t.to)
      if (sIdx == null || tIdx == null) {
        return []
      }
      return [
        {
          source: sIdx,
          target: tIdx,
          value: t.count,
          color: getStateHexColor(t.from),
          __fromState: t.from,
          __toState: t.to,
        },
      ]
    })

    return { nodes, links }
  }

  const getSourceNodeIndex = (lp: any): number | undefined => {
    if (lp?.source && typeof lp.source === "object" && typeof lp.source.index === "number") {
      return lp.source.index
    }
    if (lp?.link?.source && typeof lp.link.source.index === "number") {
      return lp.link.source.index
    }
    if (typeof lp?.index === "number" && sankeyData?.links?.[lp.index]) {
      const src = (sankeyData.links[lp.index] as any).source
      if (typeof src === "number") {
        return src
      }
      if (src && typeof src.index === "number") {
        return src.index
      }
    }
    return undefined
  }

  const useHover = () => {
    const [hoverNode, setHoverNode] = React.useState<number | null>(null)
    const [hoverLink, setHoverLink] = React.useState<number | null>(null)
    return { hoverNode, setHoverNode, hoverLink, setHoverLink }
  }
  const { hoverNode, setHoverNode, hoverLink, setHoverLink } = useHover()

  const PrettySankeyNode: React.FC<any> = (props) => {
    const { x, y, width, height, payload, index, onMouseEnter, onMouseLeave, reportLayout } = props
    const color = payload.fill || "#9CA3AF"

    useEffect(() => {
      if (!reportLayout) {
        return
      }
      const cx = Number(x) + Number(width) / 2
      reportLayout({
        index,
        name: payload?.name ?? "",
        color,
        centerX: cx,
      })
    }, [index, x, width, payload?.name, color, reportLayout])

    return (
      <g onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} style={{ cursor: "pointer" }}>
        <defs>
          <filter id="sankey-shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.25" />
          </filter>
        </defs>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          rx={0}
          ry={0}
          fill={color}
          filter="url(#sankey-shadow)"
          stroke="rgba(0,0,0,.12)"
          strokeWidth={0.5}
        />
      </g>
    )
  }

  const buildThickPath = ({ sourceX, sourceY, targetX, targetY, linkWidth }: any) => {
    const half = Math.max(1, (linkWidth ?? 1) / 2)
    const xi = (sourceX + targetX) / 2
    return [
      `M ${sourceX},${sourceY - half}`,
      `C ${xi},${sourceY - half} ${xi},${targetY - half} ${targetX},${targetY - half}`,
      `L ${targetX},${targetY + half}`,
      `C ${xi},${targetY + half} ${xi},${sourceY + half} ${sourceX},${sourceY + half}`,
      "Z",
    ].join(" ")
  }

  const nf = new Intl.NumberFormat("tr-TR")

  const PrettySankeyLink: React.FC<any> = (props) => {
    const {
      onClick,
      index,
      source,
      target,
      value,
      onMouseEnter,
      onMouseLeave,
      isDimmed,
      path,
      d,
      link,
      sourceX,
      sourceY,
      targetX,
      targetY,
      linkWidth,
    } = props

    const colorFromData = (sankeyData?.links?.[index] as any)?.color as string | undefined

    const srcIdx = getSourceNodeIndex(props)
    const colorFromNode =
      (srcIdx != null && (sankeyData?.nodes?.[srcIdx] as any)?.fill) ||
      (srcIdx != null && (sankeyData?.nodes?.[srcIdx] as any)?.state
        ? getStateHexColor((sankeyData?.nodes?.[srcIdx] as any).state)
        : undefined)

    const sColor = colorFromData || colorFromNode || "#94A3B8"

    const pathD =
      path ||
      d ||
      (link && (link.path || link.d)) ||
      buildThickPath({ sourceX, sourceY, targetX, targetY, linkWidth })

    const cx = (Number(sourceX) + Number(targetX)) / 2
    const cy = (Number(sourceY) + Number(targetY)) / 2

    const valueText = nf.format(value ?? 0)
    const showLabel = (value ?? 0) >= 3
    const labelW = Math.max(36, valueText.length * 8 + 12)

    return (
      <g
        opacity={isDimmed ? 0.18 : 0.95}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        style={{ cursor: "pointer", pointerEvents: "all" }}
      >
        <path d={pathD} fill={sColor} style={{ fill: sColor }} />
        <path d={pathD} fill="none" stroke="rgba(0,0,0,.10)" strokeWidth={0.6} />

        {showLabel && (
          <>
            <rect
              x={cx - labelW / 2}
              y={cy - 10}
              width={labelW}
              height={20}
              rx={10}
              fill="rgba(255,255,255,.95)"
              stroke="rgba(15,23,42,.08)"
            />
            <text
              x={cx}
              y={cy}
              dominantBaseline="middle"
              textAnchor="middle"
              fontSize={11}
              fill="#0f172a"
            >
              {valueText}
            </text>
          </>
        )}
      </g>
    )
  }

  const SankeyTooltipContent: React.FC<any> = ({ active, payload, label, data }) => {
    if (!active || !payload?.length) {
      return null
    }
    const d = payload[0].payload
    const sName = d.source?.name ?? ""
    const tName = d.target?.name ?? ""
    const value = d.value ?? 0

    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm shadow">
        <div className="font-medium text-slate-900">
          {sName} → {tName}
        </div>
        <div className="mt-1 text-slate-700">
          Akış: <b>{new Intl.NumberFormat("tr-TR").format(value)}</b>
        </div>
      </div>
    )
  }

  const getWeekNumber = (date: Date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
  }

  // Güvenli tarih yardımcıları
  const toTS = (v: string | Date) => (v instanceof Date ? v.getTime() : new Date(v).getTime())
  const startOfDayISO = (iso: string) => {
    const d = new Date(iso)
    d.setHours(0, 0, 0, 0)
    return d.toISOString()
  }
  const endOfDayISO = (iso: string) => {
    const d = new Date(iso)
    d.setHours(23, 59, 59, 999)
    return d.toISOString()
  }

  const computeBrandsForCell = (
    type: "total" | "incoming" | "outgoing",
    state: string,
    startISO: string,
    endISO: string,
  ) => {
    const start = toTS(startISO)
    const end = toTS(endISO)
    const result: { brand_id: string; brandName?: string }[] = []

    const data = brandStateData?.data ?? []
    for (const b of data) {
      const hist = (b.stateHistory ?? [])
        .map((h) => ({ ...h, ts: toTS(h.timestamp || h.date) }))
        .sort((a, c) => a.ts - c.ts)

      if (!hist.length) {
        continue
      }

      if (type === "total") {
        // dönem sonuna kadar son state bu mu?
        const last = hist.filter((h) => h.ts <= end).pop()
        if (last?.state === state) {
          result.push({ brand_id: b.brandId, brandName: b.brandName })
        }
        continue
      }

      // incoming/outgoing için aralıkta gerçekleşen geçişleri tara
      for (let i = 1; i < hist.length; i++) {
        const prev = hist[i - 1]
        const cur = hist[i]
        if (cur.ts < start || cur.ts > end) {
          continue
        }

        if (type === "incoming" && cur.state === state) {
          result.push({ brand_id: b.brandId, brandName: b.brandName })
          break // aynı markayı iki kez eklemeyelim
        }
        if (type === "outgoing" && prev.state === state && cur.state !== state) {
          result.push({ brand_id: b.brandId, brandName: b.brandName })
          break
        }
      }
    }
    return result
  }

  // Modal aç
  const openBrandList = (
    type: "total" | "incoming" | "outgoing",
    state: string,
    startISO: string,
    endISO: string,
    periodLabel: string,
  ) => {
    const brands = computeBrandsForCell(type, state, startISO, endISO)
    setSelectedList({ type, state, periodLabel, dateRange: { startISO, endISO }, brands })
    setShowBrandModal(true)
  }

  const closeBrandModal = () => {
    setShowBrandModal(false)
    setSelectedList(null)
  }

  const renderDurationTable = () => {
    if (!durationRecords.length) {
      return null
    }

    const periods = durationRecords
    const orderedStates = getAllStates()

    return (
      <div className="bg-white rounded-xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex items-center space-x-3">
            <div className="bg-gray-700 rounded-lg p-2">
              <span className="text-white text-lg">⏱️</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Durum Süre İstatistikleri</h2>
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-auto relative" style={{ maxHeight: "70vh" }}>
          <table className="min-w-full">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-0 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                  Durum
                </th>
                {periods.map((p) => (
                  <th
                    key={p.date}
                    colSpan={6}
                    className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200"
                  >
                    {viewMode === "monthly" ? (
                      new Date(p.date).toLocaleDateString("tr-TR", {
                        month: "long",
                        year: "numeric",
                      })
                    ) : viewMode === "weekly" ? (
                      <>
                        Hafta {getWeekNumber(new Date(p.date))}
                        <div className="text-xs font-normal text-gray-500 mt-1">
                          {new Date(p.date).toLocaleDateString("tr-TR", {
                            day: "2-digit",
                            month: "2-digit",
                          })}
                        </div>
                      </>
                    ) : (
                      new Date(p.date).toLocaleDateString("tr-TR", {
                        year: "2-digit",
                        month: "2-digit",
                        day: "2-digit",
                      })
                    )}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200"></th>
                {periods.map((p) => (
                  <React.Fragment key={`sub-${p.date}`}>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                      Ort.
                    </th>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Min
                    </th>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Maks
                    </th>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Medyan
                    </th>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Toplam Olay
                    </th>
                    <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Toplam Süre
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orderedStates.map((state) => (
                <tr key={state} className="hover:bg-gray-50">
                  <td className="px-0 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                    <span
                      className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStateColor(
                        state,
                      )}`}
                    >
                      {getBrandStateLabel(state)}
                    </span>
                  </td>
                  {periods.map((p) => {
                    const m = p.state_durations?.[state]
                    const toNum = (v: number | undefined) => (typeof v === "number" ? v : 0)
                    const avg = toNum(m?.average_duration)
                    const min = toNum(m?.min_duration)
                    const max = toNum(m?.max_duration)
                    const med = toNum(m?.median_duration)
                    const occ = toNum(m?.total_occurrences)
                    const tot = toNum(m?.total_duration)
                    return (
                      <React.Fragment key={`${p.date}-${state}`}>
                        <td className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium">
                          {avg.toFixed(1)}
                        </td>
                        <td className="px-0 py-4 whitespace-nowrap text-right">{min.toFixed(1)}</td>
                        <td className="px-0 py-4 whitespace-nowrap text-right">{max.toFixed(1)}</td>
                        <td className="px-0 py-4 whitespace-nowrap text-right">{med.toFixed(1)}</td>
                        <td className="px-0 py-4 whitespace-nowrap text-right">
                          {new Intl.NumberFormat("tr-TR").format(occ)}
                        </td>
                        <td className="px-0 py-4 whitespace-nowrap text-right">{tot.toFixed(1)}</td>
                      </React.Fragment>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderCurrentStateTable = () => {
    if (!currentStateDistribution || Object.keys(currentStateDistribution).length === 0) {
      return null
    }

    const orderedStates = getAllStates()
    const total = getTotalBrands() || 1

    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h2 className="text-xl font-bold flex items-center gap-2">📊 Durum Dağılımı</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {orderedStates.map((state) => {
              const count = currentStateDistribution[state] ?? 0
              return (
                <div key={state} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{getBrandStateLabel(state)}</span>
                    <span className="text-lg font-bold text-blue-600">{formatNumber(count)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${(count / total) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {((count / total) * 100).toFixed(1)}% of total
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  const handleQuickSelect = (days: number) => {
    const endDateObj = new Date()
    let startDateObj: Date

    if (days === -1) {
      startDateObj = new Date("2020-01-01")
    } else {
      startDateObj = new Date()
      startDateObj.setDate(endDateObj.getDate() - days)
    }

    setStartDate(startDateObj)
    setEndDate(endDateObj)
    setSelectedDays(days)
    loadStatistics()
  }

  const handleDateChange = () => {
    loadStatistics()
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("tr-TR").format(num)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR")
  }

  const getCurrentStateDistribution = () => {
    if (!brandStateData?.data) {
      return {}
    }

    const distribution: { [key: string]: number } = {}
    brandStateData.data.forEach((brand) => {
      distribution[brand.currentState] = (distribution[brand.currentState] || 0) + 1
    })
    return distribution
  }

  const getStateTransitions = () => {
    if (!brandStateData?.data) {
      return []
    }

    const transitions: { from: string; to: string; count: number }[] = []
    const transitionMap: { [key: string]: { [key: string]: number } } = {}

    brandStateData.data.forEach((brand) => {
      for (let i = 1; i < brand.stateHistory.length; i++) {
        const fromState = brand.stateHistory[i - 1].state
        const toState = brand.stateHistory[i].state

        if (!transitionMap[fromState]) {
          transitionMap[fromState] = {}
        }
        if (!transitionMap[fromState][toState]) {
          transitionMap[fromState][toState] = 0
        }
        transitionMap[fromState][toState]++
      }
    })

    Object.keys(transitionMap).forEach((fromState) => {
      Object.keys(transitionMap[fromState]).forEach((toState) => {
        transitions.push({
          from: fromState,
          to: toState,
          count: transitionMap[fromState][toState],
        })
      })
    })

    return transitions.sort((a, b) => b.count - a.count)
  }

  const getTotalBrands = () => {
    return brandStateData?.data?.length || 0
  }

  const getAllStates = () => {
    const states = new Set<string>()
    dailyData.forEach((day) => {
      Object.keys(day.stateStats).forEach((state) => {
        states.add(state)
      })
    })

    const orderedStates = [
      "registered_has_no_campaigns",
      "campaign_in_draft",
      "campaign_created_balance_not_loaded",
      "loaded_balance_has_no_campaigns",
      "campaign_created_has_no_applications",
      "campaign_created_balance_loaded_has_no_applications",
      "campaign_created_received_applications_profile_not_approved",
      "campaign_created_received_applications_profile_approved",
      "campaign_created_received_applications_collaboration_completed",
      "has_balance_loaded",
      "loaded_balance_multiple_times",
    ]

    return orderedStates.filter((state) => states.has(state))
  }

  const sankeyData = useMemo<SankeyGraphT>(() => {
    const base = transitionsForView.length ? transitionsForView : getStateTransitions()
    return buildSankeyFromTransitions(base, sankeyTopN, 1)
  }, [transitionsForView, sankeyTopN, brandStateData])

  const renderMonthlyTable = () => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-500 rounded-lg p-2">
            <span className="text-white text-lg">📅</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Aylık Marka Durum İstatistikleri</h2>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto relative" style={{ maxHeight: "70vh" }}>
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-0 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                Durum
              </th>
              {monthlyData.map((month) => (
                <th
                  key={`${month.year}-${month.monthNumber}`}
                  colSpan={4}
                  className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200"
                >
                  {new Date(month.year, month.monthNumber - 1).toLocaleDateString("tr-TR", {
                    month: "long",
                    year: "numeric",
                  })}
                </th>
              ))}
            </tr>
            {/* Sub Header - Toplam Marka ve State Değişimleri */}
            <tr>
              <th className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                {/* Boş */}
              </th>
              {monthlyData.map((month) => (
                <React.Fragment key={`${month.year}-${month.monthNumber}`}>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                    Toplam Marka
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State&apos;e Geçen
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State&apos;den Çıkan
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Değişim
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getAllStates().map((state) => (
              <tr key={state} className="hover:bg-gray-50">
                <td className="px-0 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                  <span
                    className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStateColor(
                      state,
                    )}`}
                  >
                    {getBrandStateLabel(state)}
                  </span>
                </td>
                {monthlyData.map((month) => {
                  const monthStat = month.stats[state] || 0
                  const monthTransition = month.transitions[state] || {
                    incoming: 0,
                    outgoing: 0,
                  }
                  const change = monthTransition.incoming - monthTransition.outgoing
                  const changeColor =
                    change > 0
                      ? "bg-green-100 text-green-800"
                      : change < 0
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  return (
                    <React.Fragment key={`${month.year}-${month.monthNumber}`}>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "total",
                            state,
                            monthlyData.find(
                              (m) => m.year === month.year && m.monthNumber === month.monthNumber,
                            )!.startDate,
                            monthlyData.find(
                              (m) => m.year === month.year && m.monthNumber === month.monthNumber,
                            )!.endDate,
                            new Date(month.year, month.monthNumber - 1).toLocaleDateString(
                              "tr-TR",
                              { month: "long", year: "numeric" },
                            ),
                          )
                        }
                      >
                        {monthStat.toLocaleString()}
                      </td>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "incoming",
                            state,
                            month.startDate,
                            month.endDate,
                            new Date(month.year, month.monthNumber - 1).toLocaleDateString(
                              "tr-TR",
                              { month: "long", year: "numeric" },
                            ),
                          )
                        }
                      >
                        {monthTransition.incoming > 0
                          ? monthTransition.incoming.toLocaleString()
                          : "0"}
                      </td>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "outgoing",
                            state,
                            month.startDate,
                            month.endDate,
                            new Date(month.year, month.monthNumber - 1).toLocaleDateString(
                              "tr-TR",
                              { month: "long", year: "numeric" },
                            ),
                          )
                        }
                      >
                        {monthTransition.outgoing > 0
                          ? monthTransition.outgoing.toLocaleString()
                          : "0"}
                      </td>
                      <td
                        className={`px-0 py-4 whitespace-nowrap text-right font-medium ${changeColor}`}
                      >
                        {change > 0 ? `+${change}` : change}
                      </td>
                    </React.Fragment>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderWeeklyTable = () => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-500 rounded-lg p-2">
            <span className="text-white text-lg">📅</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">
            Haftalık Marka Durum İstatistikleri
          </h2>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto relative" style={{ maxHeight: "70vh" }}>
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {/* Main Header - Haftalar */}
            <tr>
              <th className="px-0 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                Durum
              </th>
              {weeklyData.map((week) => (
                <th
                  key={week.weekNumber}
                  colSpan={4}
                  className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200"
                >
                  Hafta {week.weekNumber}
                  <div className="text-xs font-normal text-gray-500 mt-1">
                    {new Date(week.startDate).toLocaleDateString("tr-TR", {
                      day: "2-digit",
                      month: "2-digit",
                    })}{" "}
                    -{" "}
                    {new Date(week.endDate).toLocaleDateString("tr-TR", {
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </div>
                </th>
              ))}
            </tr>
            {/* Sub Header - Toplam Marka ve State Değişimleri */}
            <tr>
              <th className="px-0 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                {/* Boş */}
              </th>
              {weeklyData.map((week) => (
                <React.Fragment key={week.weekNumber}>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                    Toplam Marka
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State&apos;e Geçen
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State&apos;den Çıkan
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Değişim
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {getAllStates().map((state) => (
              <tr key={state} className="hover:bg-gray-50">
                <td className="px-0 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                  <span
                    className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStateColor(
                      state,
                    )}`}
                  >
                    {getBrandStateLabel(state)}
                  </span>
                </td>
                {weeklyData.map((week) => {
                  const weekStat = week.stats[state] || 0
                  const weekTransition = week.transitions[state] || {
                    incoming: 0,
                    outgoing: 0,
                  }
                  const change = weekTransition.incoming - weekTransition.outgoing
                  const changeColor =
                    change > 0
                      ? "bg-green-100 text-green-800"
                      : change < 0
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  return (
                    <React.Fragment key={week.weekNumber}>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "total",
                            state,
                            week.startDate,
                            week.endDate,
                            `Hafta ${week.weekNumber}`,
                          )
                        }
                      >
                        {weekStat.toLocaleString()}
                      </td>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "incoming",
                            state,
                            week.startDate,
                            week.endDate,
                            `Hafta ${week.weekNumber}`,
                          )
                        }
                      >
                        {weekTransition.incoming > 0
                          ? weekTransition.incoming.toLocaleString()
                          : "0"}
                      </td>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "outgoing",
                            state,
                            week.startDate,
                            week.endDate,
                            `Hafta ${week.weekNumber}`,
                          )
                        }
                      >
                        {weekTransition.outgoing > 0
                          ? weekTransition.outgoing.toLocaleString()
                          : "0"}
                      </td>
                      <td
                        className={`px-0 py-4 whitespace-nowrap text-right font-medium ${changeColor}`}
                      >
                        {change > 0 ? `+${change}` : change}
                      </td>
                    </React.Fragment>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderDailyTable = () => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-500 rounded-lg p-2">
            <span className="text-white text-lg">📋</span>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Günlük Marka Durum İstatistikleri</h2>
        </div>
      </div>
      <div className="overflow-x-auto overflow-y-auto relative" style={{ maxHeight: "100vh" }}>
        <table className="min-w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            {/* Main Header - Tarihler */}
            <tr>
              <th className="px-1 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                Durum
              </th>
              {dailyData.map((day) => (
                <th
                  key={day.date}
                  colSpan={4}
                  className="px-0 py-4 text-center text-sm font-semibold text-gray-700 uppercase tracking-wider border-l border-gray-200 bg-gray-50"
                >
                  {new Date(day.date).toLocaleDateString("tr-TR", {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                  })}
                </th>
              ))}
            </tr>
            {/* Sub Header - Toplam Marka ve State Değişimleri */}
            <tr>
              <th className="px-1 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-30 bg-gray-50 border-r border-gray-200">
                {/* Boş */}
              </th>
              {dailyData.map((day) => (
                <React.Fragment key={day.date}>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200 bg-gray-50">
                    Toplam Marka
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    State&apos;e Geçen
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    State&apos;den Çıkan
                  </th>
                  <th className="px-0 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                    Değişim
                  </th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {allStates.map((state) => (
              <tr key={state} className="hover:bg-gray-50">
                <td className="px-1 py-4 whitespace-nowrap font-medium sticky left-0 z-5 bg-white border-r border-gray-200 hover:bg-gray-50">
                  <span
                    className={`inline-flex px-2 py-1 text-sm font-semibold rounded-full ${getStateColor(
                      state,
                    )}`}
                  >
                    {getBrandStateLabel(state)}
                  </span>
                </td>
                {dailyData.map((day) => {
                  const sISO = startOfDayISO(day.date)
                  const eISO = endOfDayISO(day.date)

                  const totalBrands = computeBrandsForCell("total", state, sISO, eISO).length
                  const incomingBrands = computeBrandsForCell("incoming", state, sISO, eISO).length
                  const outgoingBrands = computeBrandsForCell("outgoing", state, sISO, eISO).length

                  const change = incomingBrands - outgoingBrands
                  const changeColor =
                    change > 0
                      ? "bg-green-100 text-green-800"
                      : change < 0
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-800"
                  return (
                    <React.Fragment key={day.date}>
                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "total",
                            state,
                            sISO,
                            eISO,
                            new Date(day.date).toLocaleDateString("tr-TR"),
                          )
                        }
                      >
                        {totalBrands.toLocaleString()}
                      </td>

                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "incoming",
                            state,
                            sISO,
                            eISO,
                            new Date(day.date).toLocaleDateString("tr-TR"),
                          )
                        }
                      >
                        {incomingBrands.toLocaleString()}
                      </td>

                      <td
                        className="px-0 py-4 whitespace-nowrap text-right border-l border-gray-200 font-medium cursor-pointer hover:bg-blue-50 transition-colors text-blue-600 hover:text-blue-800"
                        onClick={() =>
                          openBrandList(
                            "outgoing",
                            state,
                            sISO,
                            eISO,
                            new Date(day.date).toLocaleDateString("tr-TR"),
                          )
                        }
                      >
                        {outgoingBrands.toLocaleString()}
                      </td>

                      <td
                        className={`px-0 py-4 whitespace-nowrap text-right font-medium ${changeColor}`}
                      >
                        {change > 0 ? `+${change}` : change}
                      </td>
                    </React.Fragment>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const getStateColor = (state: string) => {
    const colors: { [key: string]: string } = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      suspended: "bg-gray-100 text-gray-800",
      approved: "bg-blue-100 text-blue-800",
      rejected: "bg-red-100 text-red-800",
    }
    return colors[state] || "bg-gray-100 text-gray-800"
  }

  const prepareDataForDownload = (mode: "daily" | "weekly" | "monthly" = viewMode): string[][] => {
    if (!brandStateData?.data) {
      return []
    }

    const downloadData: string[][] = []

    if (mode === "daily") {
      const headers = ["Durum"]
      dailyData.forEach((day) => {
        const dateStr = new Date(day.date).toLocaleDateString("tr-TR", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
        })
        headers.push(
          `${dateStr} - Toplam Marka`,
          `${dateStr} - State'e Geçen`,
          `${dateStr} - State'den Çıkan`,
          `${dateStr} - Değişim`,
        )
      })

      downloadData.push(headers)

      const allStates = getAllStates()
      allStates.forEach((state) => {
        const row = [getBrandStateLabel(state)]
        dailyData.forEach((day) => {
          const stateCount = day.stateStats[state] || 0
          const incomingCount = day.stateTransitions.reduce((total, t) => {
            if (t.to === state) {
              return total + t.count
            }
            return total
          }, 0)
          const outgoingCount = day.stateTransitions.reduce((total, t) => {
            if (t.from === state) {
              return total + t.count
            }
            return total
          }, 0)
          const change = incomingCount - outgoingCount

          row.push(
            stateCount.toString(),
            incomingCount.toString(),
            outgoingCount.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    } else if (mode === "weekly") {
      const headers = ["Durum"]
      weeklyData.forEach((week) => {
        const weekStr = `Hafta ${week.weekNumber} (${new Date(week.startDate).toLocaleDateString(
          "tr-TR",
          {
            day: "2-digit",
            month: "2-digit",
          },
        )} - ${new Date(week.endDate).toLocaleDateString("tr-TR", {
          day: "2-digit",
          month: "2-digit",
        })})`
        headers.push(
          `${weekStr} - Toplam Marka`,
          `${weekStr} - State'e Geçen`,
          `${weekStr} - State'den Çıkan`,
          `${weekStr} - Değişim`,
        )
      })

      downloadData.push(headers)

      const allStates = getAllStates()
      allStates.forEach((state) => {
        const row = [getBrandStateLabel(state)]
        weeklyData.forEach((week) => {
          const weekStat = week.stats[state] || 0
          const weekTransition = week.transitions[state] || {
            incoming: 0,
            outgoing: 0,
          }
          const change = weekTransition.incoming - weekTransition.outgoing

          row.push(
            weekStat.toString(),
            weekTransition.incoming.toString(),
            weekTransition.outgoing.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    } else {
      const headers = ["Durum"]
      monthlyData.forEach((month) => {
        const monthStr = new Date(month.year, month.monthNumber - 1).toLocaleDateString("tr-TR", {
          month: "long",
          year: "numeric",
        })
        headers.push(
          `${monthStr} - Toplam Marka`,
          `${monthStr} - State'e Geçen`,
          `${monthStr} - State'den Çıkan`,
          `${monthStr} - Değişim`,
        )
      })

      downloadData.push(headers)

      const allStates = getAllStates()
      allStates.forEach((state) => {
        const row = [getBrandStateLabel(state)]
        monthlyData.forEach((month) => {
          const monthStat = month.stats[state] || 0
          const monthTransition = month.transitions[state] || {
            incoming: 0,
            outgoing: 0,
          }
          const change = monthTransition.incoming - monthTransition.outgoing

          row.push(
            monthStat.toString(),
            monthTransition.incoming.toString(),
            monthTransition.outgoing.toString(),
            change.toString(),
          )
        })
        downloadData.push(row)
      })
    }

    return downloadData
  }

  const exportToCSV = () => {
    const data = prepareDataForDownload()
    if (data.length === 0) {
      return
    }

    const csvContent = data.map((row) => row.join(",")).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const link = document.createElement("a")
    const url = URL.createObjectURL(blob)
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `marka_durum_istatistikleri_${viewMode}_${new Date().toISOString().split("T")[0]}.csv`,
    )
    link.style.visibility = "hidden"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const computeBrandsForFlow = (
    fromState: string,
    toState: string,
    startISO: string,
    endISO: string,
  ) => {
    const start = toTS(startISO)
    const end = toTS(endISO)
    const result: { brand_id: string; brandName?: string }[] = []
    const seen = new Set<string>()

    const data = brandStateData?.data ?? []
    for (const b of data) {
      const hist = (b.stateHistory ?? [])
        .map((h) => ({ ...h, ts: toTS(h.timestamp || h.date) }))
        .sort((a, c) => a.ts - c.ts)

      for (let i = 1; i < hist.length; i++) {
        const prev = hist[i - 1]
        const cur = hist[i]
        if (cur.ts < start || cur.ts > end) {
          continue
        }
        if (prev.state === fromState && cur.state === toState) {
          if (!seen.has(b.brandId)) {
            seen.add(b.brandId)
            result.push({ brand_id: b.brandId, brandName: b.brandName })
          }
          break
        }
      }
    }
    return result
  }

  const openBrandFlowList = (
    fromState: string,
    toState: string,
    startISO: string,
    endISO: string,
    periodLabel: string,
  ) => {
    const brands = computeBrandsForFlow(fromState, toState, startISO, endISO)
    setSelectedList({
      type: "flow",
      state: toState,
      fromState,
      periodLabel,
      dateRange: { startISO, endISO },
      brands,
    })
    setShowBrandModal(true)
  }

  if (loading && !brandStateData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span>İstatistikler yükleniyor...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={loadStatistics}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    )
  }

  const currentStateDistribution = getCurrentStateDistribution()
  const stateTransitions = getStateTransitions()
  const allStates = getAllStates()

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">🏢 Marka Durum İstatistikleri</h1>
            <p className="text-blue-100 mt-2">
              Marka durumlarının detaylı analizi ve karşılaştırması
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <button
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedDays === -1
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
                onClick={() => handleQuickSelect(-1)}
              >
                Tüm Aralık
              </button>
              <button
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedDays === 1
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
                onClick={() => handleQuickSelect(1)}
              >
                Son 1 Gün
              </button>
              <button
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedDays === 7
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
                onClick={() => handleQuickSelect(7)}
              >
                Son 7 Gün
              </button>
              <button
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedDays === 30
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
                onClick={() => handleQuickSelect(30)}
              >
                Son 30 Gün
              </button>
              <button
                className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedDays === 90
                    ? "bg-white/20 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/15"
                }`}
                onClick={() => handleQuickSelect(90)}
              >
                Son 90 Gün
              </button>
            </div>

            <div className="flex items-center space-x-2 bg-white/10 rounded-lg px-3 py-2">
              <span className="text-sm text-white">📅</span>

              <DatePicker
                selected={startDate}
                onChange={(date: Date) => setStartDate(date)}
                className="bg-transparent border-none outline-none text-sm flex-1 min-w-[120px] appearance-none"
                wrapperClassName="flex-1"
                dateFormat="dd/MM/yyyy"
                maxDate={endDate}
                title="Başlangıç tarihi"
                customInput={
                  <input
                    className="bg-transparent border-none outline-none text-sm flex-1 min-w-[120px] appearance-none"
                    style={{
                      color: "white",
                      WebkitTextFillColor: "white",
                    }}
                    placeholder="Başlangıç"
                  />
                }
              />

              <span className="text-white text-sm">–</span>

              <DatePicker
                selected={endDate}
                onChange={(date: Date) => setEndDate(date)}
                className="bg-transparent border-none outline-none text-sm flex-1 min-w-[120px] appearance-none"
                wrapperClassName="flex-1"
                dateFormat="dd/MM/yyyy"
                minDate={startDate}
                maxDate={new Date()}
                title="Bitiş tarihi"
                customInput={
                  <input
                    className="bg-transparent border-none outline-none text-sm flex-1 min-w-[120px] appearance-none"
                    style={{
                      color: "white",
                      WebkitTextFillColor: "white",
                    }}
                    placeholder="Bitiş"
                  />
                }
              />

              <button
                className="bg-white/20 hover:bg-white/30 rounded px-2 py-1 text-white text-sm transition-colors"
                onClick={handleDateChange}
                disabled={loading}
                title="Tarih aralığını uygula"
              >
                Uygula
              </button>
            </div>

            <button
              className="bg-white/10 hover:bg-white/20 rounded-lg p-2 transition-colors"
              onClick={loadStatistics}
              disabled={loading}
              title="Yenile"
            >
              <span className={`text-sm ${loading ? "animate-spin" : ""}`}>🔄</span>
            </button>

            {brandStateData && (
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg transition-colors flex items-center space-x-2 text-sm"
                onClick={exportToCSV}
                title="CSV İndir"
              >
                <span>📄</span>
                <span>CSV</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {brandStateData && (
        <>
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-lg p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-indigo-500 rounded-lg p-2">
                <span className="text-white text-lg">📊</span>
              </div>
              <h2 className="text-xl font-semibold text-indigo-900">Tarih Aralığı Bilgileri</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-indigo-500">📅</span>
                  <p className="text-sm font-medium text-indigo-700">Başlangıç Tarihi</p>
                </div>
                <p className="font-semibold text-gray-900">
                  {formatDate(brandStateData.dateRange.startDate)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-indigo-500">📅</span>
                  <p className="text-sm font-medium text-indigo-700">Bitiş Tarihi</p>
                </div>
                <p className="font-semibold text-gray-900">
                  {formatDate(brandStateData.dateRange.endDate)}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-indigo-500">🏢</span>
                  <p className="text-sm font-medium text-indigo-700">Toplam Marka</p>
                </div>
                <p className="font-semibold text-gray-900">{formatNumber(getTotalBrands())}</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100">Toplam Marka</p>
                  <p className="text-3xl font-bold">{formatNumber(getTotalBrands())}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100">Mevcut Durumlar</p>
                  <p className="text-3xl font-bold">
                    {formatNumber(Object.keys(currentStateDistribution).length)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100">Durum Değişiklikleri</p>
                  <p className="text-3xl font-bold">{formatNumber(stateTransitions.length)}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100">Toplam Değişiklikler</p>
                  <p className="text-3xl font-bold">
                    {formatNumber(
                      brandStateData.data.reduce((sum, brand) => sum + brand.totalStateChanges, 0),
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-teal-100">Bakiye Yüklenen</p>
                  <p className="text-3xl font-bold">
                    {formatNumber(
                      brandStateData.data.filter((brand) => brand.currentHasBalanceLoaded).length,
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100">Çoklu Yükleme</p>
                  <p className="text-3xl font-bold">
                    {formatNumber(
                      brandStateData.data.filter((brand) => brand.currentHasMultipleBalanceLoads)
                        .length,
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Current State Distribution */}
          {renderCurrentStateTable()}
          {/* Balance Loading Statistics */}
          {brandStateData && (
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  💰 Bakiye Yükleme İstatistikleri
                </h2>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 border rounded-lg bg-gradient-to-r from-teal-50 to-teal-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-teal-800">
                        Bakiye Yüklenen Markalar
                      </span>
                      <span className="text-lg font-bold text-teal-600">
                        {formatNumber(
                          brandStateData.data.filter((brand) => brand.currentHasBalanceLoaded)
                            .length,
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-teal-200 rounded-full h-2">
                      <div
                        className="bg-teal-600 h-2 rounded-full"
                        style={{
                          width: `${
                            (brandStateData.data.filter((brand) => brand.currentHasBalanceLoaded)
                              .length /
                              getTotalBrands()) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-teal-600 mt-1">
                      {(
                        (brandStateData.data.filter((brand) => brand.currentHasBalanceLoaded)
                          .length /
                          getTotalBrands()) *
                        100
                      ).toFixed(1)}
                      % of total
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg bg-gradient-to-r from-indigo-50 to-indigo-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-indigo-800">
                        Çoklu Yükleme Yapan Markalar
                      </span>
                      <span className="text-lg font-bold text-indigo-600">
                        {formatNumber(
                          brandStateData.data.filter(
                            (brand) => brand.currentHasMultipleBalanceLoads,
                          ).length,
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-indigo-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{
                          width: `${
                            (brandStateData.data.filter(
                              (brand) => brand.currentHasMultipleBalanceLoads,
                            ).length /
                              getTotalBrands()) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-indigo-600 mt-1">
                      {(
                        (brandStateData.data.filter((brand) => brand.currentHasMultipleBalanceLoads)
                          .length /
                          getTotalBrands()) *
                        100
                      ).toFixed(1)}
                      % of total
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg bg-gradient-to-r from-amber-50 to-amber-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-sm text-amber-800">
                        Bakiye Yüklenmemiş Markalar
                      </span>
                      <span className="text-lg font-bold text-amber-600">
                        {formatNumber(
                          brandStateData.data.filter((brand) => !brand.currentHasBalanceLoaded)
                            .length,
                        )}
                      </span>
                    </div>
                    <div className="w-full bg-amber-200 rounded-full h-2">
                      <div
                        className="bg-amber-600 h-2 rounded-full"
                        style={{
                          width: `${
                            (brandStateData.data.filter((brand) => !brand.currentHasBalanceLoaded)
                              .length /
                              getTotalBrands()) *
                            100
                          }%`,
                        }}
                      ></div>
                    </div>
                    <div className="text-xs text-amber-600 mt-1">
                      {(
                        (brandStateData.data.filter((brand) => !brand.currentHasBalanceLoaded)
                          .length /
                          getTotalBrands()) *
                        100
                      ).toFixed(1)}
                      % of total
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Table View Toggle */}
          <div className="flex items-center justify-between bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="flex items-center space-x-3">
              <span className="text-gray-700 font-medium">📊 Görünüm Modu:</span>
              <div className="flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2">
                <span className="text-sm">👁️</span>
                <select
                  className="bg-transparent text-gray-700 border-none outline-none text-sm font-medium"
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value as "daily" | "weekly" | "monthly")}
                >
                  <option value="daily" className="text-gray-800">
                    Günlük Görünüm
                  </option>
                  <option value="weekly" className="text-gray-800">
                    Haftalık Görünüm
                  </option>
                  <option value="monthly" className="text-gray-800">
                    Aylık Görünüm
                  </option>
                </select>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {viewMode === "daily"
                ? "Günlük detaylı istatistikler"
                : viewMode === "weekly"
                ? "Haftalık özet istatistikler"
                : "Aylık özet istatistikler"}
            </div>
          </div>
          {/* Table View */}
          {viewMode === "monthly"
            ? renderMonthlyTable()
            : viewMode === "weekly"
            ? renderWeeklyTable()
            : renderDailyTable()}

          {/* State Duration Statistics */}
          {renderDurationTable()}
          {dailyData.length > 0 && sankeyData.links.length > 0 && (
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
              <div className="p-4 border-b flex items-center justify-between">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  🔀 Durum Geçişleri (Sankey)
                </h2>
                <div className="flex items-center gap-3 text-sm">
                  <label className="text-gray-600">Gösterilecek akış:</label>
                  <input
                    type="number"
                    min={5}
                    max={30}
                    value={sankeyTopN}
                    onChange={(e) => setSankeyTopN(Number(e.target.value || 12))}
                    className="w-16 border rounded px-2 py-1"
                    title="En güçlü kaç akış gösterilsin?"
                  />
                  <span className="text-gray-500">
                    {formatDate(brandStateData.dateRange.startDate)} –{" "}
                    {formatDate(brandStateData.dateRange.endDate)}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-slate-50 rounded-b-lg">
                <div className="h-[420px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <Sankey
                      data={sankeyData}
                      nodeWidth={18}
                      nodePadding={28}
                      linkCurvature={0.5}
                      iterations={64}
                      node={(nodeProps: any) => (
                        <PrettySankeyNode
                          {...nodeProps}
                          reportLayout={reportNodeLayout}
                          onMouseEnter={() => setHoverNode(nodeProps.index)}
                          onMouseLeave={() => setHoverNode(null)}
                        />
                      )}
                      link={(linkProps: any) => {
                        const linkData = sankeyData.links[linkProps.index] as any
                        const fromStateMeta = linkData?.__fromState
                        const toStateMeta = linkData?.__toState
                        const srcIdx =
                          typeof linkProps?.source?.index === "number"
                            ? linkProps.source.index
                            : typeof linkData?.source === "number"
                            ? linkData.source
                            : linkData?.source?.index
                        const tgtIdx =
                          typeof linkProps?.target?.index === "number"
                            ? linkProps.target.index
                            : typeof linkData?.target === "number"
                            ? linkData.target
                            : linkData?.target?.index
                        const fromState =
                          fromStateMeta ??
                          (typeof srcIdx === "number"
                            ? (sankeyData.nodes[srcIdx] as any)?.state
                            : undefined)
                        const toState =
                          toStateMeta ??
                          (typeof tgtIdx === "number"
                            ? (sankeyData.nodes[tgtIdx] as any)?.state
                            : undefined)

                        const sISO = startOfDayISO(brandStateData.dateRange.startDate)
                        const eISO = endOfDayISO(brandStateData.dateRange.endDate)
                        const periodLabel = `${formatDate(
                          brandStateData.dateRange.startDate,
                        )} – ${formatDate(brandStateData.dateRange.endDate)}`
                        const colorFromData = (sankeyData.links[linkProps.index] as any)?.color
                        const colorFromNode =
                          typeof srcIdx === "number"
                            ? (sankeyData.nodes[srcIdx] as any)?.fill
                            : undefined
                        const sColor = colorFromData || colorFromNode || "#94A3B8"

                        const connectedToHoverNode =
                          hoverNode !== null &&
                          (linkProps.source?.index === hoverNode ||
                            linkProps.target?.index === hoverNode)

                        const isDimmed =
                          hoverLink !== null
                            ? linkProps.index !== hoverLink
                            : hoverNode !== null
                            ? !connectedToHoverNode
                            : false

                        return (
                          <PrettySankeyLink
                            {...linkProps}
                            isDimmed={isDimmed}
                            onMouseEnter={() => setHoverLink(linkProps.index)}
                            onMouseLeave={() => setHoverLink(null)}
                            onClick={() => {
                              if (fromState && toState) {
                                openBrandFlowList(fromState, toState, sISO, eISO, periodLabel)
                              }
                            }}
                            style={{ fill: sColor }}
                          />
                        )
                      }}
                    >
                      <RechartsTooltip content={<SankeyTooltipContent data={sankeyData} />} />
                    </Sankey>
                  </ResponsiveContainer>
                </div>
                {legendList.length > 0 && (
                  <div className="mt-3 -mx-4 px-4 py-3 border-t border-slate-200 bg-slate-50">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      {legendList.map((n) => (
                        <div
                          key={n.index}
                          className="flex items-center gap-2 px-2.5 py-1 rounded-full border border-slate-200 bg-white text-slate-700 text-xs shadow-sm"
                          title={n.name}
                        >
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full"
                            style={{ background: n.color }}
                          />
                          <span className="truncate max-w-[200px] sm:max-w-[260px]">{n.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {showBrandModal && selectedList && (
            <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold">Marka Detayları</h3>
                  <button
                    onClick={closeBrandModal}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>
                <div className="mt-2 text-blue-100">
                  {selectedList.type === "flow" ? (
                    <>
                      <p>
                        <span className="font-medium">Geçiş:</span>{" "}
                        {getBrandStateLabel(selectedList.fromState!)} →{" "}
                        {getBrandStateLabel(selectedList.state)}
                      </p>
                      <p>
                        <span className="font-medium">Tip:</span> Geçiş
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <span className="font-medium">Durum:</span>{" "}
                        {getBrandStateLabel(selectedList.state)}
                      </p>
                      <p>
                        <span className="font-medium">Tip:</span>{" "}
                        {selectedList.type === "total"
                          ? "Toplam Marka"
                          : selectedList.type === "incoming"
                          ? "State'e Geçen"
                          : "State'den Çıkan"}
                      </p>
                    </>
                  )}
                  <p>
                    <span className="font-medium">Dönem:</span> {selectedList.periodLabel}
                  </p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 min-h-0">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">
                    Markalar ({selectedList.brands.length})
                  </h4>
                  <div className="flex flex-col gap-3">
                    {selectedList.brands.map((brand, index) => (
                      <a
                        key={brand.brand_id}
                        className="cursor-pointer gap-2"
                        href={`/brands/find/${brand.brand_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3">
                            <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {brand.brandName || brand.brand_id}
                              </p>
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Toplam: {selectedList.brands.length.toLocaleString()} marka
                  </div>
                  <button
                    onClick={closeBrandModal}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default BrandStats
